import { Button } from '@sunwebgroup/uds-ui/button'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import { TimeSelector } from '@/scopes/checkout/components/services/GolfService/GolfServiceSlotSheet/TimeSelector'
import {
  cancelGolfSlotEditing,
  saveGolfSlot,
} from '@/scopes/checkout/domain/actions/services/golf'
import { useService } from '@/scopes/checkout/domain/hooks'
import { ServiceType } from '@/scopes/checkout/domain/types'
import { BuggyFilter } from '../Filters/BuggyFilter'
import { DateFilter } from '../Filters/DateFilter'
import { HolesFilter } from '../Filters/HolesFilter'
import { LocationFilter } from '../Filters/LocationFilter'
import { golfSlotIsComplete } from '../helpers'
import {
  golfEditingValueForSlotSelector,
  golfLocationIsBuggyEligibleSelector,
} from '../selectors'
import { golfSlotIsBeingEdited, useGolfServiceStore } from '../store'
import styles from './GolfServiceSlotSheet.module.css'
import { ParticipantsSelector } from './ParticipantsSelector'

interface GolfServiceSlotSheetProps {
  slotId: string
}

export function GolfServiceSlotSheet({ slotId }: GolfServiceSlotSheetProps) {
  const intl = useIntl()
  const { service, setServiceLocalValue, setServiceValue } = useService(ServiceType.Golf)

  const { endEditingSlot } = useGolfServiceStore()
  const isEditing = useGolfServiceStore(golfSlotIsBeingEdited(slotId))

  const slot = golfEditingValueForSlotSelector(service, slotId)
  const isBuggyEligible = golfLocationIsBuggyEligibleSelector(service, slot?.locationId ?? null)
  const isComplete = golfSlotIsComplete(slot)

  const selectedOption = slot?.optionId ? service?.options[slot.optionId] : null
  const totalPrice = selectedOption
    ? selectedOption.withBuggy
      ? selectedOption.price
      : selectedOption.price * (slot?.participantIds.length ?? 0)
    : 0

  function handleCloseSheet() {
    setServiceLocalValue(cancelGolfSlotEditing({ slotId }))
    endEditingSlot(slotId)
  }

  function handleSave() {
    if (!service)
      return
    if (!isComplete || !slot)
      return
    const updatedService = saveGolfSlot({ slotId })(service)
    setServiceValue(updatedService)
    endEditingSlot(slotId)
  }

  if (!service)
    return null

  return (
    <Sheet.Root open={isEditing} onOpenChange={open => !open && handleCloseSheet()}>
      <Sheet.Body side="right">
        <Sheet.Header
          titleLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_golf_sheet_title' })}
          closeLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_golf_cancel_button' })}
        />
        <Sheet.Content className={styles.sheetContent}>
          <div className={styles.filters}>
            <LocationFilter />
            <HolesFilter />
            <DateFilter />
            <TimeSelector />
            {isBuggyEligible && <BuggyFilter />}
          </div>
          <ParticipantsSelector />
          {totalPrice > 0 && (
            <div className={styles.totalPrice}>
              <Typography variant="body-s-regular-flat" className={styles.totalPriceLabel}>
                {intl.formatMessage({ id: 'checkout_steps_ancillaries_golf_total_price_label' })}
              </Typography>
              <Typography variant="body-m-bold-flat">
                {intl.formatCurrency(totalPrice, { showSign: true })}
              </Typography>
            </div>
          )}
        </Sheet.Content>
        <Sheet.Footer>
          <div className={styles.actions}>
            <Button variant="primary" disabled={!isComplete} onClick={handleSave} fullWidth>
              {intl.formatMessage({ id: 'checkout_steps_ancillaries_golf_save_button' })}
            </Button>
          </div>
        </Sheet.Footer>
      </Sheet.Body>
    </Sheet.Root>
  )
}
